import {
  alpha,
  Card,
  CardActionArea,
  Collapse,
  IconButton,
  lighten,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  areDatesClose,
  formatReadableDate,
} from "portal/utils/categoryCollection/plantProfile";
import { classes } from "portal/theme/theme";
import { PathHighlight, useVersionHistory } from "./VersionHistoryProvider";
import { SavedVersionMetadata } from "protos/portal/category_profile";
import { useTranslation } from "react-i18next";
import { VersionUser } from "./VersionUser";
import BranchIcon from "@mui/icons-material/CallSplit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import RevertIcon from "@mui/icons-material/Undo";

interface BaseProps {
  highlight?: PathHighlight;
}

export interface VersionMetadata extends SavedVersionMetadata {
  previousActiveVersionId?: string;
}
interface VersionCardProps extends BaseProps {
  versions: VersionMetadata[];
  onClick: (versionUuid: string) => void;
}
export const VersionCard: FC<VersionCardProps> = ({
  versions,
  highlight,
  onClick,
}) => {
  const muiTheme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const { selectedVersionId } = useVersionHistory();

  const isHighlighted = useCallback(
    (versionId: string) =>
      (highlight
        ? [highlight.start, ...highlight.between, highlight.end]
        : []
      ).includes(versionId),
    [highlight]
  );

  const prevSelectedVersionId = useRef<string>("");
  useEffect(() => {
    if (
      prevSelectedVersionId.current !== selectedVersionId &&
      versions.some((v) => v.versionId === selectedVersionId) &&
      !expanded
    ) {
      setExpanded(true);
    }
    prevSelectedVersionId.current = selectedVersionId;
  }, [expanded, selectedVersionId, versions]);

  if (versions.length === 0) {
    return;
  }
  const representativeVersion = versions[0]!;
  const isSingleItem = versions.length <= 1;

  const borderClasses = "border-r-2 border-y-0 border-l-0 border-solid";
  const highlightBorderColor = {
    borderColor:
      highlight?.type === "revert"
        ? muiTheme.palette.error.main
        : muiTheme.palette.secondary.main,
  };
  const highlightSummaryBorderColor = {
    borderColor: lighten(highlightBorderColor.borderColor, 0.5),
  };
  let summaryStyle =
    !expanded && versions.some((v) => isHighlighted(v.versionId))
      ? highlightSummaryBorderColor
      : undefined;
  if (
    highlight &&
    [highlight.start, highlight.end].includes(representativeVersion.versionId)
  ) {
    summaryStyle = highlightBorderColor;
  }

  return isSingleItem ? (
    <VersionItem
      cardClassName={classes("pl-8", {
        [borderClasses]: isHighlighted(representativeVersion.versionId),
      })}
      cardStyle={
        isHighlighted(representativeVersion.versionId)
          ? highlightBorderColor
          : undefined
      }
      version={representativeVersion}
      outlined
      highlight={highlight}
      onClick={onClick}
    />
  ) : (
    <Card
      variant="outlined"
      className={classes("bg-transparent", {
        "border-primary-300 border-solid border-1":
          selectedVersionId === representativeVersion.versionId,
      })}
    >
      <CardActionArea
        component="div"
        onClick={() => {
          onClick(representativeVersion.versionId);
        }}
      >
        <div
          className={classes("flex items-start pb-4", {
            [borderClasses]: summaryStyle,
          })}
          style={summaryStyle}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
            aria-expanded={expanded}
            size="small"
            className={classes("mt-2", { "-rotate-90": !expanded })}
          >
            <ExpandMoreIcon />
          </IconButton>
          <div className="min-w-0 flex-1 pt-4 pr-4">
            <VersionItemContent
              version={representativeVersion}
              highlight={highlight}
            />
          </div>
        </div>
      </CardActionArea>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {versions.map((version) =>
          version.versionId === representativeVersion.versionId ? undefined : (
            <VersionItem
              version={version}
              cardClassName={classes("pl-12", {
                [borderClasses]: isHighlighted(version.versionId),
              })}
              cardStyle={
                isHighlighted(version.versionId)
                  ? highlightBorderColor
                  : undefined
              }
              key={version.versionId}
              highlight={highlight}
              onClick={onClick}
            />
          )
        )}
      </Collapse>
    </Card>
  );
};

interface VersionItemProps extends BaseProps {
  version: VersionMetadata;
  cardClassName?: string;
  cardStyle?: CSSProperties;
  outlined?: boolean;
  onClick: (versionUuid: string) => void;
}
const VersionItem: FC<VersionItemProps> = ({
  version,
  cardClassName,
  cardStyle,
  outlined,
  highlight,
  onClick,
}) => {
  const { selectedVersionId } = useVersionHistory();
  const muiTheme = useTheme();
  let bgColor = muiTheme.palette.background.section;
  if (highlight?.end === version.versionId) {
    bgColor =
      highlight.type === "parent"
        ? muiTheme.palette.secondary.light
        : muiTheme.palette.error.light;
  }
  return (
    <Card
      variant={outlined ? "outlined" : undefined}
      className={
        selectedVersionId === version.versionId
          ? "border-primary-300 border-solid border-1"
          : undefined
      }
      style={{ backgroundColor: bgColor ? alpha(bgColor, 0.3) : undefined }}
    >
      <CardActionArea
        className={classes("pl-4 pr-4 py-4", cardClassName)}
        style={cardStyle}
        onClick={() => {
          onClick(version.versionId);
        }}
      >
        <VersionItemContent version={version} highlight={highlight} />
      </CardActionArea>
    </Card>
  );
};

interface VersionItemContentProps extends BaseProps {
  version: VersionMetadata;
}

const VersionItemContent: FC<VersionItemContentProps> = ({
  version: {
    versionId,
    activeTime,
    user,
    clientVersionCreateTime,
    parentVersionId,
    revertVersionId,
    previousActiveVersionId,
  },
  highlight,
}) => {
  const { t, i18n } = useTranslation();
  const muiTheme = useTheme();

  const { currentVersionId, onShowParent, onShowRevert } = useVersionHistory();

  const parentActionId =
    parentVersionId && parentVersionId !== previousActiveVersionId
      ? parentVersionId
      : undefined;

  return (
    <div className="flex w-full items-start gap-2">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Typography variant="body2">
          {activeTime && formatReadableDate(activeTime, i18n)}
        </Typography>
        {currentVersionId === versionId && (
          <Typography
            variant="body2"
            color="textSecondary"
            className="italic text-xs"
          >
            {t("components.categoryCollectionProfile.history.currentVersion")}
          </Typography>
        )}
        {user && <VersionUser user={user} />}
        {activeTime &&
        clientVersionCreateTime &&
        areDatesClose(clientVersionCreateTime, activeTime) ? (
          <Typography color="textSecondary" variant="body2" className="text-xs">
            {t("components.categoryCollectionProfile.history.recorded", {
              clientVersionCreateTime: formatReadableDate(
                clientVersionCreateTime,
                i18n
              ),
            })}
          </Typography>
        ) : undefined}
      </div>
      {(parentActionId || revertVersionId) && (
        <div className="flex shrink-0">
          {parentActionId && (
            <Tooltip
              title={t(
                "components.categoryCollectionProfile.history.actions.seeParentTip"
              )}
            >
              <IconButton
                size="small"
                className="self-start p-0.5"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onShowParent(versionId, parentActionId);
                }}
                style={{
                  backgroundColor:
                    highlight?.type === "parent" &&
                    highlight.start === versionId
                      ? muiTheme.palette.secondary.main
                      : undefined,
                  color:
                    highlight?.type === "parent" &&
                    highlight.start === versionId
                      ? muiTheme.palette.text.primary
                      : undefined,
                }}
              >
                <BranchIcon />
              </IconButton>
            </Tooltip>
          )}
          {revertVersionId && (
            <Tooltip
              title={t(
                "components.categoryCollectionProfile.history.actions.seeRevertTip"
              )}
            >
              <IconButton
                size="small"
                className="self-start p-0.5"
                color="error"
                style={{
                  backgroundColor:
                    highlight?.type === "revert" &&
                    highlight.start === versionId
                      ? muiTheme.palette.error.main
                      : undefined,
                  color:
                    highlight?.type === "revert" &&
                    highlight.start === versionId
                      ? muiTheme.palette.text.primary
                      : undefined,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onShowRevert(versionId, revertVersionId);
                }}
              >
                <RevertIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};
